import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Check, Crosshair, User, Users } from 'lucide-react';
import { toast } from 'react-toastify';

import { WsDropdown, WsSegmented } from 'src/stage/WsControls';
import { StageDialog } from '../shared/StageKit';
import { apiMessage, stageTones } from '../shared/crmWork';
import { createTarget, updateTarget } from './targetsApi';
import { METRICS, PERIODS, formatValue, unitWord } from './targetMeta';

// AUTO-CRM-32: set or change a target in plain words: what to count, whose it
// is, how often, and the goal.
const DESCRIPTIONS = {
  new_leads: 'Leads added from any source.',
  leads_contacted: 'Different leads reached by call, WhatsApp, email or meeting.',
  calls: 'Calls logged as done.',
  conversations: 'Calls, messages, emails and meetings logged as done.',
  meetings: 'Meetings logged as done.',
  stage_reached: 'Leads that enter a stage you pick, like Site visit.',
  won: 'Leads moved to a won stage.',
  revenue: 'The value of won leads, from a field like Deal value.',
};
const DAYS = { daily: 1, weekly: 7, monthly: 30, quarterly: 91 };
const SUGGESTED = { new_leads: { daily: 30, weekly: 200, monthly: 1000, quarterly: 3000 }, leads_contacted: { daily: 25, weekly: 150, monthly: 600, quarterly: 1800 }, calls: { daily: 40, weekly: 200, monthly: 800, quarterly: 2400 },
  conversations: { daily: 60, weekly: 300, monthly: 1200, quarterly: 3600 }, meetings: { daily: 3, weekly: 15, monthly: 60, quarterly: 180 }, stage_reached: { daily: 5, weekly: 25, monthly: 100, quarterly: 300 },
  won: { daily: 1, weekly: 5, monthly: 20, quarterly: 60 }, revenue: { daily: 100000, weekly: 500000, monthly: 2000000, quarterly: 6000000 } };

export default function TargetDialog({ options, target = null, defaults = {}, onClose, onSaved }) {
  const editing = Boolean(target);
  const me = options?.me;
  const canCompany = Boolean(options?.can_manage_company);
  const canPeople = Boolean(options?.can_manage_people);
  const people = options?.people || [];
  const pipelines = options?.pipelines || [];
  const initialOwner = target ? (target.user ? (target.user.id === me ? 'me' : 'person') : 'company') : (defaults.owner || (canCompany ? 'company' : 'me'));
  const [metric, setMetric] = useState(target?.metric || defaults.metric || 'calls');
  const [period, setPeriod] = useState(target?.period || defaults.period || 'daily');
  const [owner, setOwner] = useState(initialOwner);
  const [personId, setPersonId] = useState(target?.user && target.user.id !== me ? target.user.id : defaults.personId || people.find((person) => person.id !== me)?.id || '');
  const [goal, setGoal] = useState(String(target?.target_value ?? defaults.value ?? SUGGESTED[metric]?.[period] ?? ''));
  const [goalTouched, setGoalTouched] = useState(editing || defaults.value !== undefined);
  const [pipelineId, setPipelineId] = useState(target?.pipeline?.id || target?.stage?.pipeline_id || '');
  const [stageId, setStageId] = useState(target?.stage?.id ? String(target.stage.id) : '');
  const [valueFieldId, setValueFieldId] = useState(target?.value_field?.id || options?.value_fields?.[0]?.id || '');
  const [name, setName] = useState(target?.name || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Until the person types a goal, suggest a round number for the choice.
  useEffect(() => { if (!goalTouched && SUGGESTED[metric]?.[period]) setGoal(String(SUGGESTED[metric][period])); }, [metric, period, goalTouched]);
  const stagePipeline = pipelines.find((pipeline) => pipeline.id === (pipelineId || pipelines.find((item) => item.is_default)?.id)) || pipelines[0];
  // A lost stage is never something to aim for.
  const stages = (stagePipeline?.stages || []).filter((stage) => !(stage.is_closed && Number(stage.probability) < 100));
  const tones = useMemo(() => stageTones(stages), [stages]);
  const preview = { metric, unit: METRICS[metric]?.unit, target_value: Number(goal) || 0, stage: stages.find((stage) => String(stage.id) === stageId) };
  const perDay = Number(goal) > 0 && period !== 'daily' ? Number(goal) / DAYS[period] : null;

  const ownerItems = [
    ...(canCompany || (editing && initialOwner === 'company') ? [{ id: 'company', label: 'Company', icon: Building2 }] : []),
    { id: 'me', label: 'Me', icon: User },
    ...(canPeople && people.some((person) => person.id !== me) ? [{ id: 'person', label: 'Someone else', icon: Users }] : []),
  ];

  const save = async () => {
    const value = Number(goal);
    if (!value || value < 1) { setError('Enter a goal of at least 1.'); return; }
    if (metric === 'stage_reached' && !stageId) { setError('Pick the stage this target counts.'); return; }
    if (metric === 'revenue' && !valueFieldId) { setError('Pick the field that holds the deal value.'); return; }
    const body = {
      name: name.trim() || null,
      metric,
      period,
      target_value: value,
      user_id: owner === 'company' ? null : owner === 'me' ? me : personId,
      pipeline_id: metric === 'stage_reached' ? stagePipeline?.id || null : pipelineId || null,
      stage_id: metric === 'stage_reached' ? Number(stageId) : null,
      value_field_id: metric === 'revenue' ? valueFieldId : null,
    };
    setBusy(true);
    setError('');
    try {
      if (editing) await updateTarget(target.id, body); else await createTarget(body);
      toast.success(editing ? 'Target updated' : 'Target set. Progress updates as your team works.');
      onSaved?.();
      onClose();
    } catch (saveError) {
      setError(apiMessage(saveError, 'We couldn’t save this target.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <StageDialog
      title={editing ? 'Edit target' : 'Set a target'}
      subtitle="Pick what to count and the goal. Progress fills in from the work your team logs."
      icon={Crosshair}
      width={680}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          {error ? <p className="crm-kit-error" role="alert">{error}</p> : <span className="ws-grow ws-muted">{Number(goal) > 0 ? `Goal: ${formatValue(preview, goal)} ${unitWord(preview, Number(goal))} ${PERIODS[period].this === 'today' ? 'every day' : `every ${PERIODS[period].noun}`}.` : ''}</span>}
          <button type="button" className="ws-btn ws-btn--quiet" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="ws-btn ws-btn--primary" onClick={save} disabled={busy}><Check size={15} />{busy ? 'Saving…' : editing ? 'Save target' : 'Set target'}</button>
        </>
      )}
    >
      <div className="crm-kit-section">
        <span className="crm-kit-label">What do you want to count?</span>
        <div className="crm-tmetrics" role="radiogroup" aria-label="What to count">
          {Object.entries(METRICS).map(([key, item]) => (
            <button key={key} type="button" role="radio" aria-checked={metric === key} className={`crm-tmetric${metric === key ? ' is-on' : ''}`} onClick={() => { setMetric(key); setError(''); }}>
              <span className="crm-tmetric-icon" aria-hidden="true"><item.icon size={15} /></span>
              <span className="ws-grow"><b>{item.label}</b><small>{DESCRIPTIONS[key]}</small></span>
            </button>
          ))}
        </div>
      </div>

      {metric === 'stage_reached' && (
        <div className="crm-kit-section">
          <span className="crm-kit-label">Which stage?</span>
          {pipelines.length > 1 && <WsDropdown label="Pipeline" value={stagePipeline?.id || ''} onChange={(value) => { setPipelineId(value); setStageId(''); }} options={pipelines.map((pipeline) => ({ value: pipeline.id, label: pipeline.name }))} />}
          <div className="crm-chip-row" role="radiogroup" aria-label="Stage">
            {stages.map((stage) => (
              <button key={stage.id} type="button" role="radio" aria-checked={String(stage.id) === stageId} className={`crm-choice crm-choice--stage${String(stage.id) === stageId ? ' is-on' : ''}`} style={{ '--tone': tones[stage.id]?.color }} onClick={() => setStageId(String(stage.id))}>
                <i aria-hidden="true" />{stage.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {metric === 'revenue' && (
        <div className="crm-kit-section">
          <span className="crm-kit-label">Which field holds the deal value?</span>
          {(options?.value_fields || []).length
            ? <WsDropdown label="Value field" value={valueFieldId} onChange={setValueFieldId} options={options.value_fields.map((field) => ({ value: field.id, label: field.label }))} />
            : <p className="ws-muted crm-kit-hint">Add a number or currency field (such as Deal value) in Lead table first.</p>}
        </div>
      )}

      <div className="crm-kit-grid">
        <div className="crm-kit-section">
          <span className="crm-kit-label">Whose target?</span>
          <WsSegmented label="Whose target" value={owner} onChange={setOwner} items={ownerItems} />
          {owner === 'person' && <WsDropdown label="Person" value={personId} onChange={setPersonId} options={people.filter((person) => person.id !== me).map((person) => ({ value: person.id, label: person.name }))} />}
        </div>
        <div className="crm-kit-section">
          <span className="crm-kit-label">How often?</span>
          <WsSegmented label="Period" value={period} onChange={setPeriod} items={Object.entries(PERIODS).map(([id, item]) => ({ id, label: item.label }))} />
        </div>
      </div>

      <div className="crm-kit-grid">
        <label className="crm-kit-section">
          <span className="crm-kit-label">Goal {PERIODS[period].this === 'today' ? 'each day' : `each ${PERIODS[period].noun}`}</span>
          <span className="crm-tgoal">
            {metric === 'revenue' && <em aria-hidden="true">₹</em>}
            <input className="ws-input crm-kit-input" type="number" min={1} step={metric === 'revenue' ? 1000 : 1} inputMode="numeric" value={goal} onChange={(event) => { setGoal(event.target.value); setGoalTouched(true); setError(''); }} aria-label="Goal" />
            {metric !== 'revenue' && <em aria-hidden="true">{unitWord(preview, Number(goal) || 2)}</em>}
          </span>
          {perDay !== null && <span className="ws-muted crm-kit-hint">About {formatValue(preview, metric === 'revenue' || perDay >= 1 ? Math.round(perDay) : Math.round(perDay * 10) / 10)} {unitWord(preview, perDay)} a day.</span>}
        </label>
        {metric !== 'stage_reached' && pipelines.length > 1 && (
          <div className="crm-kit-section">
            <span className="crm-kit-label">Count leads from</span>
            <WsDropdown label="Pipeline" value={pipelineId} onChange={setPipelineId} options={[{ value: '', label: 'All pipelines', short: 'All' }, ...pipelines.map((pipeline) => ({ value: pipeline.id, label: pipeline.name }))]} />
          </div>
        )}
      </div>

      <label className="crm-kit-section">
        <span className="crm-kit-label">Name (optional)</span>
        <input className="ws-input crm-kit-input" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} placeholder={metric === 'stage_reached' && preview.stage ? `Leads reaching ${preview.stage.name}` : METRICS[metric].label} />
      </label>
    </StageDialog>
  );
}
